// ===== Session Manager for Segment (Guaranteed Ordering) =====
// Global APIs exposed: window.sessionCore and a Segment gate on window.analytics

// ---- Segment pre-gate: create a stable stub with buffering BEFORE Segment loads ----
(function ensureSegmentStub() {
  // Keep a single stub we control until Segment rewires it
  var a = window.analytics;
  if (!a || !Array.isArray(a)) {
    a = [];
    window.analytics = a;
  }
  if (!a.__sessionGate) {
    a.__sessionGate = {
      startedQueued: false,
      preBuffer: [], // events before "Session Started"
      postBuffer: [], // safe to push after startedQueued
    };
    // Intercept pushes until we mark startedQueued; preserve order
    var origPush = a.push;
    a.push = function () {
      var gate = window.analytics.__sessionGate;
      if (!gate.startedQueued) {
        gate.preBuffer.push([].slice.call(arguments));
      } else {
        // once started, we can push directly to the array; Segment will replay in order
        return origPush.apply(a, arguments);
      }
    };
    // Helper to flush any buffered calls once Session Started has been queued
    a.__flushBuffers = function () {
      var gate = window.analytics.__sessionGate;
      gate.preBuffer.forEach(function (args) {
        Array.prototype.push.apply(a, args);
      });
      gate.preBuffer = [];
      // postBuffer isn’t used here, but kept if you want two-phase gating
    };
  }
})();

// ---- Keys & Config ----
const K_META = "__sess_meta__";
const K_ATTRIB = "__sess_attrib__";
const K_EMITTED = "__sess_started_id__";
let SESSION_IDLE_MS = 30 * 60 * 1000;
const now = () => Date.now();

// ---- Storage helpers ----
const safeGet = (k) => {
  try {
    const v = localStorage.getItem(k);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
};
const safeSet = (k, v) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {}
};
const safeDel = (k) => {
  try {
    localStorage.removeItem(k);
  } catch {}
};

// ---- Device Info Enrichment (for all Segment events) ----
// Adds `properties.device_info` and `options.context.{device_model_hint, platform_version_hint, userAgentData}`
let __deviceHints = {
  model: null,
  platformVersion: null,
  fullVersionList: null,
  uaFullVersion: null,
  fetched: false,
};
let __deviceHintsPromise = null;

function getUserAgentDataContext() {
  try {
    const uad = navigator && navigator.userAgentData;
    if (!uad) return null;
    return {
      brands: Array.isArray(uad.brands) ? uad.brands : null,
      mobile: typeof uad.mobile === "boolean" ? uad.mobile : null,
      platform: typeof uad.platform === "string" ? uad.platform : null,
      fullVersionList: Array.isArray(__deviceHints.fullVersionList)
        ? __deviceHints.fullVersionList
        : null,
    };
  } catch {
    return null;
  }
}

function initDeviceHints() {
  if (__deviceHintsPromise) return __deviceHintsPromise;

  __deviceHintsPromise = (async () => {
    try {
      const uad = navigator && navigator.userAgentData;
      if (!uad || typeof uad.getHighEntropyValues !== "function") {
        __deviceHints.fetched = true;
        return __deviceHints;
      }

      const values = await uad.getHighEntropyValues([
        "model",
        "platformVersion",
        "fullVersionList",
        "uaFullVersion",
      ]);
      __deviceHints.model = values && typeof values.model === "string" ? values.model : null;
      __deviceHints.platformVersion =
        values && typeof values.platformVersion === "string"
          ? values.platformVersion
          : null;
      __deviceHints.fullVersionList =
        values && Array.isArray(values.fullVersionList) ? values.fullVersionList : null;
      __deviceHints.uaFullVersion =
        values && typeof values.uaFullVersion === "string" ? values.uaFullVersion : null;
      __deviceHints.fetched = true;
      return __deviceHints;
    } catch (e) {
      __deviceHints.fetched = true;
      return __deviceHints;
    }
  })();

  return __deviceHintsPromise;
}

function normalizeVersionLike(v) {
  if (!v || typeof v !== "string") return null;
  const cleaned = v.trim().replace(/_/g, ".");
  return cleaned.length ? cleaned : null;
}

function parseBrowserFromUA(ua) {
  const s = (ua || "").toString();

  // Edge (Chromium)
  let m = s.match(/\bEdg\/([0-9.]+)/);
  if (m) return { browser_name: "Edge", browser_version: m[1] };

  // Opera
  m = s.match(/\bOPR\/([0-9.]+)/);
  if (m) return { browser_name: "Opera", browser_version: m[1] };

  // Samsung Internet
  m = s.match(/\bSamsungBrowser\/([0-9.]+)/);
  if (m) return { browser_name: "Samsung Internet", browser_version: m[1] };

  // Firefox
  m = s.match(/\bFirefox\/([0-9.]+)/);
  if (m) return { browser_name: "Firefox", browser_version: m[1] };

  // Chrome (ignore if it's Edge/Opera which we already caught)
  m = s.match(/\bChrome\/([0-9.]+)/);
  if (m) return { browser_name: "Chrome", browser_version: m[1] };

  // Safari (Version/x.y)
  if (/\bSafari\//.test(s) && !/\bChrome\//.test(s)) {
    m = s.match(/\bVersion\/([0-9.]+)/);
    if (m) return { browser_name: "Safari", browser_version: m[1] };
    return { browser_name: "Safari", browser_version: null };
  }

  return { browser_name: null, browser_version: null };
}

function parseOSFromUA(ua) {
  const s = (ua || "").toString();

  // iOS
  let m = s.match(/\b(iPhone|iPad|iPod).*?\bOS ([0-9_]+)/);
  if (m) return { os_name: "iOS", os_version: normalizeVersionLike(m[2]) };

  // Android
  m = s.match(/\bAndroid ([0-9.]+)/);
  if (m) return { os_name: "Android", os_version: normalizeVersionLike(m[1]) };

  // Windows
  m = s.match(/\bWindows NT ([0-9.]+)/);
  if (m) return { os_name: "Windows", os_version: normalizeVersionLike(m[1]) };

  // macOS
  m = s.match(/\bMac OS X ([0-9_]+)/);
  if (m) return { os_name: "macOS", os_version: normalizeVersionLike(m[1]) };

  // Chrome OS
  m = s.match(/\bCrOS\b.*?\b([0-9.]+)\b/);
  if (m) return { os_name: "Chrome OS", os_version: normalizeVersionLike(m[1]) };

  // Linux
  if (/\bLinux\b/.test(s)) return { os_name: "Linux", os_version: null };

  return { os_name: null, os_version: null };
}

function parseAndroidModelFromUA(ua) {
  const s = (ua || "").toString();
  // e.g. "Android 13; Pixel 7 Pro Build/..."
  let m = s.match(/\bAndroid [^;]+;\s*([^;()]+?)\s+Build\//);
  if (m && m[1]) return m[1].trim();
  // e.g. "...; SM-G991B) AppleWebKit/..."
  m = s.match(/\bAndroid [^;]+;\s*([^;()]+?)\s*\)/);
  if (m && m[1]) return m[1].trim();
  return null;
}

function guessDeviceVendor(model, osName, ua) {
  const u = (ua || "").toString();
  if (osName === "iOS" || /\b(iPhone|iPad|iPod)\b/.test(u)) return "Apple";
  if (osName === "macOS" || /\bMacintosh\b/.test(u)) return "Apple";
  if (!model || typeof model !== "string") return null;

  if (/^(SM-|GT-|SCH-|SHV-)/i.test(model) || /\bSamsung\b/i.test(model)) return "Samsung";
  if (/\bPixel\b/i.test(model)) return "Google";
  if (/\bONEPLUS\b/i.test(model) || /\bOnePlus\b/i.test(model)) return "OnePlus";
  if (/\bRedmi\b/i.test(model) || /\bMi\b/i.test(model) || /\bPOCO\b/i.test(model))
    return "Xiaomi";
  if (/\bMoto\b/i.test(model) || /\bMotorola\b/i.test(model) || /\bXT[0-9]{3,5}\b/i.test(model))
    return "Motorola";
  if (/\bOPPO\b/i.test(model) || /\bCPH[0-9]{3,5}\b/i.test(model)) return "OPPO";
  if (/\brealme\b/i.test(model)) return "realme";
  if (/\bvivo\b/i.test(model)) return "vivo";

  return null;
}

function getDeviceType(ua) {
  try {
    const uad = navigator && navigator.userAgentData;
    if (uad && typeof uad.mobile === "boolean") {
      if (/\biPad\b/.test(ua || "")) return "tablet";
      return uad.mobile ? "mobile" : "desktop";
    }
  } catch {}

  const s = (ua || "").toString();
  if (/\biPad\b/i.test(s) || /\bTablet\b/i.test(s)) return "tablet";
  if (/\bMobi\b/i.test(s) || /\bAndroid\b/i.test(s) || /\biPhone\b/i.test(s)) return "mobile";
  return "desktop";
}

function pickBrowserFromFullVersionList(fullVersionList) {
  if (!Array.isArray(fullVersionList)) return null;

  const prefer = [
    { match: /Microsoft Edge/i, name: "Edge" },
    { match: /Opera/i, name: "Opera" },
    { match: /Samsung Internet/i, name: "Samsung Internet" },
    { match: /Brave/i, name: "Brave" },
    { match: /Vivaldi/i, name: "Vivaldi" },
    { match: /Google Chrome/i, name: "Chrome" },
    { match: /\bChromium\b/i, name: "Chromium" },
    { match: /Safari/i, name: "Safari" },
  ];

  for (const p of prefer) {
    const hit = fullVersionList.find((b) => b && typeof b.brand === "string" && p.match.test(b.brand));
    if (hit) return { browser_name: p.name, browser_version: hit.version || null };
  }

  return null;
}

function getDeviceInfo() {
  const ua = (navigator && navigator.userAgent) || "";
  const osFromUA = parseOSFromUA(ua);
  const browserFromUA = parseBrowserFromUA(ua);
  const browserFromCH = pickBrowserFromFullVersionList(__deviceHints.fullVersionList);

  const osName = osFromUA.os_name;
  const osVersion =
    (osName === "Android" && normalizeVersionLike(__deviceHints.platformVersion)) ||
    (osName === "iOS" && normalizeVersionLike(__deviceHints.platformVersion)) ||
    osFromUA.os_version ||
    null;

  const modelHint = __deviceHints.model && __deviceHints.model !== "K" ? __deviceHints.model : null;
  const model = modelHint || (osName === "Android" ? parseAndroidModelFromUA(ua) : null);

  const browser_name = (browserFromCH && browserFromCH.browser_name) || browserFromUA.browser_name;
  const browser_version =
    (browserFromCH && normalizeVersionLike(browserFromCH.browser_version)) ||
    normalizeVersionLike(browserFromUA.browser_version) ||
    normalizeVersionLike(__deviceHints.uaFullVersion) ||
    null;

  return {
    device_type: getDeviceType(ua),
    device_vendor: guessDeviceVendor(model, osName, ua),
    device_model: model || null,
    os_name: osName || null,
    os_version: osVersion || null,
    browser_name: browser_name || null,
    browser_version: browser_version || null,
  };
}

function getDeviceContext() {
  // Kick off hint fetch (best-effort) without blocking event emission
  initDeviceHints();
  return {
    device: getDeviceInfo(),
    device_model_hint: __deviceHints.model || null,
    platform_version_hint: __deviceHints.platformVersion || null,
    userAgentData: getUserAgentDataContext(),
  };
}

function enrichOptionsWithDeviceContext(options) {
  const base = options && typeof options === "object" ? options : {};
  const ctx = getDeviceContext();
  return { ...base, context: { ...(base.context || {}), ...ctx } };
}

// ---- User Traits Enrichment (Liquid → window.__segmentUserTraits) ----
// Reads traits emitted by snippets/segment-user-traits.liquid and injects
// them into context.traits on every Segment track/page/identify call.
// Always returns the four keys; values are null when the customer is logged
// out or the field is missing, so downstream destinations see a stable shape.
function getUserTraits() {
  const src =
    (typeof window !== "undefined" && window.__segmentUserTraits) || {};
  const pick = (v) => (typeof v === "string" && v ? v : null);
  return {
    email: pick(src.email),
    first_name: pick(src.first_name),
    last_name: pick(src.last_name),
    mobile_number: pick(src.mobile_number),
  };
}

function enrichOptionsWithUserTraits(options) {
  const traits = getUserTraits();
  const base = options && typeof options === "object" ? options : {};
  const ctx = base.context || {};
  // Caller-supplied traits win over Liquid-rendered defaults.
  return {
    ...base,
    context: { ...ctx, traits: { ...traits, ...(ctx.traits || {}) } },
  };
}

// Single composed enrichment used by all Segment call sites.
function enrichOptions(options) {
  return enrichOptionsWithUserTraits(enrichOptionsWithDeviceContext(options));
}

function isPlainObject(v) {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function normalizePageArgs(args) {
  // Segment Analytics.js: page([category], [name], [properties], [options], [callback])
  const a = Array.prototype.slice.call(args || []);
  let callback = null;
  if (a.length && typeof a[a.length - 1] === "function") callback = a.pop();

  let category = null;
  let name = null;
  let idx = 0;

  if (typeof a[0] === "string" && typeof a[1] === "string") {
    category = a[0];
    name = a[1];
    idx = 2;
  } else if (typeof a[0] === "string") {
    name = a[0];
    idx = 1;
  }

  let properties = null;
  let options = null;
  if (isPlainObject(a[idx])) {
    properties = a[idx];
    idx += 1;
  }
  if (isPlainObject(a[idx])) {
    options = a[idx];
    idx += 1;
  }

  const out = [];
  if (category !== null) out.push(category);
  if (name !== null) out.push(name);
  out.push(properties || {});
  out.push(enrichOptions(options));
  if (callback) out.push(callback);
  return out;
}

function normalizeIdentifyArgs(args) {
  // Segment Analytics.js: identify([userId], [traits], [options], [callback])
  const a = Array.prototype.slice.call(args || []);
  let callback = null;
  if (a.length && typeof a[a.length - 1] === "function") callback = a.pop();

  let idx = 0;
  let userId = null;
  if (typeof a[0] === "string" || typeof a[0] === "number") {
    userId = a[0];
    idx = 1;
  }

  let traits = null;
  let options = null;
  if (isPlainObject(a[idx])) {
    traits = a[idx];
    idx += 1;
  }
  if (isPlainObject(a[idx])) {
    options = a[idx];
    idx += 1;
  }

  const out = [];
  if (userId !== null) out.push(userId);
  if (traits !== null) out.push(traits);
  out.push(enrichOptions(options));
  if (callback) out.push(callback);
  return out;
}

function installSegmentDeviceInfoEnrichment() {
  // 1) Enrich queued calls (analytics.push([...])) before Segment loads.
  try {
    const a = window.analytics;
    if (a && Array.isArray(a) && a.__sessionGate && !a.__deviceInfoEnrichedPush) {
      const prevPush = a.push;
      a.push = function () {
        try {
          if (arguments.length === 1 && Array.isArray(arguments[0])) {
            const call = arguments[0];
            const method = call && call[0];
            if (method === "track") {
              call[3] = enrichOptions(call[3]);
            } else if (method === "identify") {
              const enriched = normalizeIdentifyArgs(call.slice(1));
              call.length = 1;
              Array.prototype.push.apply(call, enriched);
            } else if (method === "page") {
              const enriched = normalizePageArgs(call.slice(1));
              call.length = 1;
              Array.prototype.push.apply(call, enriched);
            }
          }
        } catch {}
        return prevPush.apply(a, arguments);
      };
      a.__deviceInfoEnrichedPush = true;
    }
  } catch {}

  // 2) Enrich all direct calls once Segment rewires analytics into a real object.
  function maybeWrap() {
    try {
      const a = window.analytics;
      if (!a || Array.isArray(a) || a.__deviceInfoWrapped) return false;
      if (typeof a.track !== "function") return false;

      const origTrack = a.track;
      a.track = function (event, properties, options, callback) {
        let opts = options;
        let cb = callback;
        if (typeof opts === "function") {
          cb = opts;
          opts = null;
        }
        const enrichedOpts = enrichOptions(opts);
        return origTrack.call(this, event, properties, enrichedOpts, cb);
      };

      if (typeof a.page === "function") {
        const origPage = a.page;
        a.page = function () {
          return origPage.apply(this, normalizePageArgs(arguments));
        };
      }

      if (typeof a.identify === "function") {
        const origIdentify = a.identify;
        a.identify = function () {
          return origIdentify.apply(this, normalizeIdentifyArgs(arguments));
        };
      }

      a.__deviceInfoWrapped = true;
      return true;
    } catch {
      return false;
    }
  }

  // Prefer ready() hook if available, but also poll as a fallback.
  try {
    if (window.analytics && typeof window.analytics.ready === "function") {
      window.analytics.ready(function () {
        maybeWrap();
      });
    }
  } catch {}

  let waited = 0;
  const interval = setInterval(function () {
    waited += 200;
    if (maybeWrap() || waited >= 10000) clearInterval(interval);
  }, 200);
}

// ---- ID & UTM helpers ----
const makeUID = () => {
  // Generate a unique alphanumeric string
  const timestamp = Date.now();
  const randomPart = Math.random().toString(36).substring(2, 10);
  return `${randomPart}${timestamp}`;
};

const ATTRIBUTION_QUERY_KEYS = [
  "criteo_gum_id",
  "fbclid",
  "fbc",
  "fbp",
  "_fbc",
  "_fbp",
  "gbraid",
  "gclid",
  "wbraid",
  "utm_ad",
  "utm_adgroup",
  "utm_adgroup_name",
  "utm_adid",
  "utm_adset",
  "utm_adsetid",
  "utm_campaign",
  "utm_campaign_name",
  "utm_content",
  "utm_creative",
  "utm_device",
  "utm_geo",
  "utm_landing",
  "utm_matchtype",
  "utm_medium",
  "utm_network",
  "utm_placement",
  "utm_source",
  "utm_targetid",
  "utm_term",
];

// Helper function to get cookie value
function getCookie(name) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
}

// Helper function to extract GA pseudo ID from _ga cookie
// Format: _ga=GA1.2.XXXXXXXXX.YYYYYYYY or _ga_XXXXXXXXX=GS1.1.YYYYYYYY.ZZZZZZZZ
function getGAPseudoId() {
  try {
    // Try _ga cookie first (Universal Analytics / GA4)
    const gaCookie = getCookie("_ga");
    if (gaCookie) {
      // Format: GA1.2.XXXXXXXXX.YYYYYYYY - extract the last part
      const parts = gaCookie.split(".");
      if (parts.length >= 4) {
        return parts[parts.length - 2] + "." + parts[parts.length - 1];
      }
    }

    // Try _ga_* cookies (GA4 measurement ID specific)
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.startsWith("_ga_")) {
        const value = cookie.split("=")[1];
        if (value) {
          // Format: GS1.1.YYYYYYYY.ZZZZZZZZ - extract YYYYYYYY.ZZZZZZZZ
          const parts = value.split(".");
          if (parts.length >= 4) {
            return parts[2] + "." + parts[3];
          }
        }
      }
    }
  } catch (e) {
    // Silently fail if cookie parsing fails
  }
  return null;
}

// Helper function to get or generate _fbc from fbclid
function getFBCValue(fbclid) {
  // First try to get from cookie
  const fbcCookie = getCookie("_fbc");
  if (fbcCookie) return fbcCookie;

  // If fbclid exists but cookie doesn't, generate _fbc value
  // Format: fb.1.{creationTime}.{fbclid} — Meta's Pixel/Conversions API spec
  // (https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc)
  // requires creationTime as a Unix timestamp in MILLISECONDS, not seconds.
  if (fbclid) {
    return `fb.1.${Date.now()}.${fbclid}`;
  }

  return null;
}

function parseAttributionFromURL(href = window.location.href) {
  const u = new URL(href, window.location.origin);
  const qp = u.searchParams;
  const get = (k) => qp.get(k) || null;

  // Get fbclid first (can be from URL or we'll use it to generate _fbc)
  const fbclid = get("fbclid");

  // Collect all attribution parameters - always include all keys with null if not present
  const attribution = {
    // Ad click IDs - prioritize URL params, fallback to cookies
    criteo_gum_id: get("criteo_gum_id") || getCookie("_criteo_uid") || null,
    fbclid: fbclid,
    fbc: get("_fbc") || get("fbc") || getFBCValue(fbclid),
    fbp: get("_fbp") || get("fbp") || getCookie("_fbp") || null,
    gbraid: get("gbraid"),
    gclid: get("gclid"),
    wbraid: get("wbraid"),

    // UTM parameters
    utm_ad: get("utm_ad"),
    utm_adgroup: get("utm_adgroup"),
    utm_adgroup_name: get("utm_adgroup_name"),
    utm_adid: get("utm_adid"),
    utm_adset: get("utm_adset"),
    utm_adsetid: get("utm_adsetid"),
    utm_campaign: get("gad_campaignid"),
    utm_campaign_name: get("utm_campaign"),
    utm_content: get("utm_content"),
    utm_creative: get("utm_creative"),
    utm_device: get("utm_device"),
    utm_geo: get("utm_geo"),
    utm_landing: get("utm_landing"),
    utm_matchtype: get("utm_matchtype"),
    utm_medium: get("utm_medium"),
    utm_network: get("utm_network"),
    utm_placement: get("utm_placement"),
    utm_source: get("utm_source"),
    utm_targetid: get("utm_targetid"),
    utm_term: get("utm_term"),

    // Google Analytics pseudo ID
    ga_pseudo_id: getGAPseudoId(),

    // Referrer
    referrer_url: document.referrer || null,
  };

  // Always return the full attribution object with all keys
  return attribution;
}

// Keep the old function for backward compatibility
function parseUTMFromURL(href = window.location.href) {
  const attribution = parseAttributionFromURL(href);

  // Extract basic UTM parameters for backward compatibility
  const utm = {};
  if (attribution.utm_source) utm.utm_source = attribution.utm_source;
  if (attribution.utm_medium) utm.utm_medium = attribution.utm_medium;
  if (attribution.utm_campaign) utm.utm_campaign = attribution.utm_campaign;
  if (attribution.utm_term) utm.utm_term = attribution.utm_term;
  if (attribution.utm_content) utm.utm_content = attribution.utm_content;

  // Infer from ad click IDs if no explicit UTM
  if (Object.keys(utm).length === 0) {
    if (attribution.gclid || attribution.gbraid || attribution.wbraid) {
      return { utm_source: "google", utm_medium: "cpc" };
    }
    if (attribution.fbclid) {
      return { utm_source: "facebook", utm_medium: "paid_social" };
    }
    if (attribution.criteo_gum_id) {
      return { utm_source: "criteo", utm_medium: "display" };
    }
  }

  return Object.keys(utm).length > 0 ? utm : null;
}

const shallowEqual = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  const ka = Object.keys(a),
    kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) if (a[k] !== b[k]) return false;
  return true;
};

// ---- Cross-tab (best effort) ----
let bc;
try {
  bc = new BroadcastChannel("__sess_bc__");
} catch {}
const broadcast = (msg) => {
  try {
    bc && bc.postMessage(msg);
  } catch {}
};

// ---- Meta & Attribution I/O ----
function readMeta() {
  return safeGet(K_META);
}
function writeMeta(meta) {
  safeSet(K_META, meta);
  broadcast({ t: "meta", meta });
  // Mirror {id, index} into a first-party cookie so the Shopify Web Pixels
  // sandbox ("Session Started Shopify" custom pixel, configured in Customer
  // events) can read the SAME session_id/session_index via browser.cookie —
  // the sandbox has no access to this page's real localStorage, only cookies.
  // Format must match what that pixel parses: {i: <id>, n: <index>}.
  try {
    if (meta && meta.id) {
      var cookieVal = encodeURIComponent(JSON.stringify({ i: meta.id, n: meta.index || 1 }));
      var maxAgeSec = Math.round((SESSION_IDLE_MS || 30 * 60 * 1000) / 1000);
      document.cookie = "st_sess=" + cookieVal + "; path=/; max-age=" + maxAgeSec + "; SameSite=Lax";
    }
  } catch (e) {
    // best-effort only — session_id mismatch with the pixel is degraded
    // behavior, not a page-breaking failure
  }
}
function readAttrib() {
  const stored = safeGet(K_ATTRIB);
  if (stored) return stored;

  const meta = readMeta();
  if (meta && meta.attribution) return meta.attribution;

  // Return default attribution object with all keys set to null
  return {
    criteo_gum_id: null,
    fbclid: null,
    fbc: null,
    fbp: null,
    gbraid: null,
    gclid: null,
    wbraid: null,
    utm_ad: null,
    utm_adgroup: null,
    utm_adgroup_name: null,
    utm_adid: null,
    utm_adset: null,
    utm_adsetid: null,
    utm_campaign: null,
    utm_campaign_name: null,
    utm_content: null,
    utm_creative: null,
    utm_device: null,
    utm_geo: null,
    utm_landing: null,
    utm_matchtype: null,
    utm_medium: null,
    utm_network: null,
    utm_placement: null,
    utm_source: null,
    utm_targetid: null,
    utm_term: null,
    ga_pseudo_id: null,
    referrer_url: null,
  };
}
function writeAttrib(a) {
  const normalized = a || null;
  a ? safeSet(K_ATTRIB, a) : safeDel(K_ATTRIB);
  const meta = readMeta();
  if (meta) {
    const existing = meta.attribution || null;
    const needsUpdate =
      (normalized === null && existing !== null) ||
      (normalized !== null &&
        (existing === null || !shallowEqual(existing, normalized)));
    if (needsUpdate) {
      meta.attribution = normalized;
      writeMeta(meta);
    }
  }
  broadcast({ t: "attrib", attrib: a });
}
function hasStarted(id) {
  const v = safeGet(K_EMITTED);
  return v && v.id === id;
}
function markStarted(id) {
  safeSet(K_EMITTED, { id });
}

// Self-contained copy of custom-tracking.liquid's window.getLocationData().
// session-manager.js runs synchronously at the very top of <head> (theme.liquid
// triggers ensureSessionAndMaybeStart() at line ~212), while window.getLocationData
// is only defined later, on DOMContentLoaded, by custom-tracking.liquid (rendered
// ~1300 lines further down). So the very first events of a session — "Session
// Started" and "Page Viewed" — would call it before it exists and always get
// null back, even for a returning visitor with a saved address. Reading location
// directly here removes the dependency on that later script entirely, so these
// two events get the real value (or the correct null-shape) right when they fire.
// Keep this in sync with getLocationData() in custom-tracking.liquid if that
// function's shape or storage keys ever change.
function getLocationDataSync() {
  const locationData = {
    address_selected: false,
    selected_saved_address_id: null,
    location_selection_method: null,
    full_address: null,
    latitude: null,
    longitude: null,
    pincode: null,
    city: null,
    state: null,
    location_last_updated_at: null
  };

  try {
    const sessionShippingAddress = sessionStorage.getItem('order_shipping_address');
    const localUserLocationStr = localStorage.getItem('userLocation');

    let sessionAddress = null;
    let localUserLocation = null;

    if (sessionShippingAddress) {
      try {
        sessionAddress = JSON.parse(sessionShippingAddress);
      } catch (e) {
        console.error('Error parsing sessionShippingAddress:', e);
      }
    }

    if (localUserLocationStr) {
      try {
        localUserLocation = JSON.parse(localUserLocationStr);
      } catch (e) {
        console.error('Error parsing localUserLocation:', e);
      }
    }

    const rawSource = sessionAddress?.source || localUserLocation?.source;
    const sourceEnumMap = {
      'geolocation': 'current_location',
      'google_places': 'manual_search',
      'saved_address': 'saved_address'
    };

    let full_address = null;
    if (sessionAddress) {
      const addressParts = [
        sessionAddress?.address1,
        sessionAddress?.address2,
        sessionAddress?.city,
        sessionAddress?.province,
        sessionAddress?.zip
      ].filter(part => part != null && part !== '');

      if (addressParts.length > 0) {
        full_address = addressParts.join(', ').trim();
      }
    }

    locationData.location_selection_method = sourceEnumMap[rawSource] || null;
    locationData.address_selected = locationData.location_selection_method === 'saved_address';
    locationData.selected_saved_address_id = sessionAddress?.id || localUserLocation?.addressId || null;
    locationData.full_address = full_address || localUserLocation?.fullAddress || null;
    locationData.latitude = sessionAddress?.latitude || localUserLocation?.latitude || null;
    locationData.longitude = sessionAddress?.longitude || localUserLocation?.longitude || null;
    locationData.pincode = sessionAddress?.zip || localUserLocation?.pincode || null;
    locationData.city = sessionAddress?.city || localUserLocation?.city || null;
    locationData.state = sessionAddress?.province || localUserLocation?.state || null;
    locationData.location_last_updated_at = sessionAddress?.timestamp || localUserLocation?.timestamp || null;
  } catch (error) {
    console.error('Error parsing location data:', error);
  }

  return locationData;
}

// ---- GUARANTEED ORDER: open the Segment stub gate FIRST ----
// This used to also emit the "Session Started" track event itself — that's
// now been moved to the "Session Started Shopify" Shopify Web Pixel instead
// (Customer events), so it's intentionally removed from here. Everything
// else in this function stays: the preBuffer gate that every other event on
// the site depends on (theme.liquid wraps window.analytics.push so nothing
// reaches Segment until startedQueued flips true) must keep opening at
// exactly this same point in the session lifecycle, or no event would ever
// leave preBuffer.
function queueSessionStarted(meta) {
  // Mark as started FIRST to prevent race conditions across tabs
  markStarted(meta.id);

  // Mark gate as started and flush anything buffered before
  if (
    window.analytics.__sessionGate &&
    !window.analytics.__sessionGate.startedQueued
  ) {
    window.analytics.__sessionGate.startedQueued = true;
    console.log("[session] Gate opened, flushing buffered events");
    // Flush buffered pre-events so they follow session start
    window.analytics.__flushBuffers && window.analytics.__flushBuffers();
  }
}

// ---- Core session logic ----
function ensureSessionAndMaybeStart() {
  const t = now();
  let meta = readMeta();

  if (!meta) {
    // New session: create new session_id, set index to 1, and trigger Session Started event
    const attribution0 = parseAttributionFromURL();
    meta = {
      id: makeUID(), // Create new session_id
      index: 1, // First session
      lastActiveMs: t,
      attribution: attribution0,
    };
    writeMeta(meta);
    writeAttrib(attribution0);

    // Double-check: another tab might have created a session simultaneously
    const recheck = readMeta();
    if (recheck && recheck.id !== meta.id) {
      // Another tab won the race, use their session
      meta = recheck;
    }

    // Trigger Session Started event for the new session
    // Only trigger if not already started (prevents duplicate events across tabs)
    if (!hasStarted(meta.id)) {
      queueSessionStarted(meta);
    }
    return meta;
  }

  const idle = t - (meta.lastActiveMs || 0);
  if (idle >= SESSION_IDLE_MS) {
    // Session rolled: create new session_id, increment index, and trigger Session Started event
    const attributionNew = parseAttributionFromURL();
    meta = {
      id: makeUID(), // Create new session_id
      index: Number(meta.index || 1) + 1, // Increment session index
      lastActiveMs: t,
      attribution: attributionNew,
    };
    writeMeta(meta);
    writeAttrib(attributionNew);

    // Double-check: another tab might have created a new session simultaneously
    const recheck = readMeta();
    if (
      recheck &&
      recheck.id !== meta.id &&
      recheck.lastActiveMs >= meta.lastActiveMs
    ) {
      // Another tab won the race, use their session
      meta = recheck;
    }

    // Trigger Session Started event for the rolled session
    // Only trigger if not already started (prevents duplicate events across tabs)
    if (!hasStarted(meta.id)) {
      queueSessionStarted(meta);
    }
    return meta;
  }

  // heartbeat - session still active
  meta.lastActiveMs = t;
  if (!meta.attribution) {
    meta.attribution = readAttrib();
  }
  writeMeta(meta);
  return meta;
}

// ---- Public API for theme wrapper ----
function setIdleMs(ms) {
  const n = Number(ms);
  if (Number.isFinite(n) && n > 0) SESSION_IDLE_MS = n;
}
function getSessionFields() {
  const m = readMeta() || ensureSessionAndMaybeStart();
  return { session_id: m.id, session_index: m.index };
}
function getMergedContext(extra = {}) {
  const m = ensureSessionAndMaybeStart();
  // Always read the latest attribution from storage to ensure we have the most recent value
  const attribution = readAttrib();
  const pageCtx = getPageContext();
  return {
    session_id: m.id,
    session_index: m.index,
    attribution: attribution,
    ...pageCtx,
    ...(extra || {}),
  };
}

// Page/screen context auto-injected on every Segment track/page call.
// Used to satisfy spec params (screen_name, screen_class, screen_id,
// page_location, page_path, page_id) on events like promotion_clicked
// without editing each call site.
function getPageContext() {
  try {
    const html = document.documentElement || {};
    const tmpl = html.getAttribute ? html.getAttribute('data-template') : null;
    const canonical = document.querySelector("link[rel='canonical']");
    const canonicalHref = canonical ? canonical.getAttribute('href') : null;
    return {
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_id: canonicalHref || window.location.pathname,
      screen_name: document.title || tmpl || '',
      screen_class: tmpl || '',
      screen_id: canonicalHref || window.location.pathname,
    };
  } catch (e) {
    return {};
  }
}
function updateAttributionFromURLIfChanged(url = window.location.href) {
  let parsedURL = null;
  let hasRelevantQuery = false;
  try {
    parsedURL = new URL(url, window.location.origin);
    for (const key of ATTRIBUTION_QUERY_KEYS) {
      if (parsedURL.searchParams.has(key)) {
        hasRelevantQuery = true;
        break;
      }
    }
  } catch {}

  if (!hasRelevantQuery) {
    // Even if no URL params, update GA pseudo ID and Facebook cookies if they changed
    const current = readAttrib();
    const gaPseudoId = getGAPseudoId();
    // Check URL for fbclid first, then fall back to stored value
    let fbclid = null;
    try {
      const urlParams = new URL(url, window.location.origin).searchParams;
      fbclid = urlParams.get("fbclid") || null;
    } catch {}
    if (!fbclid && current) {
      fbclid = current.fbclid || null;
    }
    const fbcValue = getFBCValue(fbclid);
    const fbpCookie = getCookie("_fbp");
    let changed = false;
    const updated = current ? { ...current } : { ...readAttrib() };

    // Update fbclid if found in URL
    if (fbclid && updated.fbclid !== fbclid) {
      updated.fbclid = fbclid;
      changed = true;
    }

    if (gaPseudoId && updated.ga_pseudo_id !== gaPseudoId) {
      updated.ga_pseudo_id = gaPseudoId;
      changed = true;
    }

    if (updated.fbc !== fbcValue) {
      updated.fbc = fbcValue;
      changed = true;
    }

    if (updated.fbp !== fbpCookie) {
      updated.fbp = fbpCookie || null;
      changed = true;
    }

    if (changed) {
      writeAttrib(updated);
    }
    return;
  }

  const incoming = parseAttributionFromURL(url);
  incoming.referrer_url = document.referrer || null;
  // Always update GA pseudo ID and Facebook cookies from cookies (they may have changed)
  incoming.ga_pseudo_id = getGAPseudoId();
  // Update _fbc - use cookie if available, otherwise generate from fbclid
  incoming.fbc = getFBCValue(incoming.fbclid);
  incoming.fbp = getCookie("_fbp") || incoming.fbp || null;

  const current = readAttrib();
  const urlKeys =
    parsedURL && parsedURL.searchParams
      ? ATTRIBUTION_QUERY_KEYS.filter((key) => parsedURL.searchParams.has(key))
      : [];
  const hasUTMKey = urlKeys.some((key) => key.startsWith("utm_"));

  // Scenario 1: No prior attribution or URL contains explicit UTMs -> treat incoming as source of truth.
  if (!current || hasUTMKey) {
    if (!shallowEqual(incoming, current)) {
      writeAttrib(incoming);
    }
    return;
  }

  // Scenario 2: Only click IDs (gclid/gbraid/etc.) present.
  // Update just the keys provided, preserving previous UTMs.
  const merged = { ...current };
  let changed = false;

  const setKey = (key, value) => {
    if (merged[key] !== value) {
      merged[key] = value;
      changed = true;
    }
  };

  if (merged.referrer_url !== incoming.referrer_url) {
    merged.referrer_url = incoming.referrer_url;
    changed = true;
  }

  // Always update GA pseudo ID and Facebook cookies if they changed
  if (merged.ga_pseudo_id !== incoming.ga_pseudo_id) {
    merged.ga_pseudo_id = incoming.ga_pseudo_id;
    changed = true;
  }

  if (merged.fbc !== incoming.fbc) {
    merged.fbc = incoming.fbc;
    changed = true;
  }

  if (merged.fbp !== incoming.fbp) {
    merged.fbp = incoming.fbp;
    changed = true;
  }

  for (const key of urlKeys) {
    setKey(key, incoming[key]);
  }

  if (changed) {
    writeAttrib(merged);
  }
}

// Keep the old function for backward compatibility
function updateUTMsFromURLIfChanged(url = window.location.href) {
  return updateAttributionFromURLIfChanged(url);
}

// Cross-tab listeners
(function wireCrossTab() {
  if (bc) {
    bc.onmessage = (e) => {
      const d = e?.data;
      if (!d) return;
      if (d.t === "meta" && d.meta) {
        const local = readMeta();
        if (!local || (d.meta.lastActiveMs || 0) >= (local.lastActiveMs || 0))
          safeSet(K_META, d.meta);
      } else if (d.t === "attrib") {
        d.attrib ? safeSet(K_ATTRIB, d.attrib) : safeDel(K_ATTRIB);
      }
    };
  }
  window.addEventListener("storage", (ev) => {
    if (ev.key === K_META && ev.newValue) {
      const incoming = JSON.parse(ev.newValue);
      const local = readMeta();
      if (!local || (incoming.lastActiveMs || 0) >= (local.lastActiveMs || 0))
        safeSet(K_META, incoming);
    } else if (ev.key === K_ATTRIB) {
      ev.newValue
        ? safeSet(K_ATTRIB, JSON.parse(ev.newValue))
        : safeDel(K_ATTRIB);
    }
  });
})();

function waitForLocationData(timeout = 1000) {
  return new Promise((resolve) => {
    const interval = 100;
    let waited = 0;
    const check = () => {
      if (typeof window.getLocationData === "function") {
        try {
          const data = window.getLocationData();
          // Resolve immediately once function exists (even if data is null)
          // This means function is initialized, so we can use it
          resolve(data);
          return true;
        } catch(e) {
          console.error('[session] Error calling getLocationData:', e);
          // Function exists but errored - resolve with null
          resolve(null);
          return true;
        }
      }
      return false;
    };

    // Check immediately
    if (check()) {
      return;
    }

    // Poll until function exists or timeout
    const timer = setInterval(() => {
      if (check()) {
        clearInterval(timer);
        return;
      }

      waited += interval;

      if (waited >= timeout) {
        clearInterval(timer);
        // Function never initialized - resolve with null
        resolve(null);
      }
    }, interval);
  });
}

// ---- Page View Tracking ----
function trackPageView(extraParams = {}) {
  console.log("[session] trackPageView called");

  // Update attribution from URL first (if UTM params are present)
  updateAttributionFromURLIfChanged();

  // Ensure session exists and Session Started is queued FIRST
  const session = ensureSessionAndMaybeStart();

  // Always read the latest attribution from storage after potential update
  // This ensures we get the most recent attribution even if session.attribution is stale
  const attribution = readAttrib();

  // Get device category from localStorage or detect
  const deviceCategory =
    localStorage.getItem("_deviceTypeIL") ||
    (window.innerWidth <= 768 ? "mobile" : "desktop");

  // Determine content group based on current page
  const path = window.location.pathname;
  let contentGroup = "other";

  if (path.includes("/products/")) {
    contentGroup = "product";
  } else if (path.includes("/collections/")) {
    contentGroup = "collection";
  } else if (path.includes("/cart")) {
    contentGroup = "cart";
  } else if (path.includes("/checkout")) {
    contentGroup = "checkout";
  } else if (path === "/" || path === "") {
    contentGroup = "home";
  } else if (path.includes("/search")) {
    contentGroup = "search";
  } else if (path.includes("/account")) {
    contentGroup = "account";
  } else if (path.includes("/pages/categories")) {
    contentGroup = "categories";
  }else if (path.includes("/pages/lifestyle")) {
    contentGroup = "lifestyle";
  }else if (path.includes("/pages/pharmacy-hub")) {
    contentGroup = "pharmacy-hub";
  }
   else if (path.includes("/pages/")) {
    contentGroup = "page";
  }

  let subContentGroup = null;
  if (window.innerWidth <= 768) {
    const segments = path.split("/").filter(Boolean);
    const isHome = path === "/" || path === "";
    const PET_DEFAULTS = { home: "all", categories: "dogs", lifestyle: "dogs", "pharmacy-hub": "all" };
    const key = isHome ? "home" : (segments[0] === "pages" ? segments[1] : null);
    if (key && PET_DEFAULTS[key]) {
      const selected =
        document.querySelector(".mweb-pettab.is-active[data-pet]") ||
        document.querySelector(".mweb-catpill--selected[data-pet]");
      if (selected) {
        subContentGroup = selected.getAttribute("data-pet");
      } else {
        try {
          subContentGroup = new URLSearchParams(window.location.search).get("pet") || null;
        } catch (e) {}
        if (!subContentGroup) subContentGroup = PET_DEFAULTS[key];
      }
    }
  }

  // Read directly via getLocationDataSync() rather than waiting on
  // window.getLocationData (only defined later, on DOMContentLoaded, by
  // custom-tracking.liquid) — Page Viewed can fire before that and still get
  // the real value now, instead of firing fast with a guaranteed-null fallback.
  const locationData = getLocationDataSync();

  const pageViewData = {
    attribution: attribution,
    content_group: contentGroup,
    sub_content_group: subContentGroup,
    device_category: deviceCategory,
    path: path,
    platform: "web",
    session_id: session.id,
    session_index: session.index,
    title: document.title,
    url: window.location.href,
    user_id: localStorage.getItem("cid") || null,
    ...(locationData && {
      user_location: locationData
    }),
    ...extraParams,
  };
  // Track the page view - this will be queued after Session Started automatically
  // because ensureSessionAndMaybeStart() above ensures Session Started is queued first
  if (window.analytics) {
    window.analytics.page(
      "Page Viewed",
      pageViewData,
      enrichOptions(null)
    );
    console.log("[session] Page Viewed event tracked with data:", pageViewData);
  } else {
    console.error(
      "[session] window.analytics not available, Page Viewed not tracked!"
    );
  }

  // sendBeacon fallback — deferred to page-hide (see enqueueBeacon). Page Viewed is a
  // PAGE call, so the beacon must go to /v1/p (type:'page'), not /v1/t.
  enqueueBeacon("page", "Page Viewed", pageViewData, pageViewData.user_id);

  return pageViewData;
}

// ---- Track Link Click ----
function trackLinkClick(eventName, eventData = {}) {
  // Update attribution from URL first (if UTM params are present)
  updateAttributionFromURLIfChanged();

  // Ensure session exists
  const session = ensureSessionAndMaybeStart();

  // Always read the latest attribution from storage after potential update
  const attribution = readAttrib();

  const clickData = {
    platform: "web",
    session_id: session.id,
    session_index: session.index,
    attribution: attribution,
    url: window.location.href,
    user_id: window.customer?.id || null,
    timestamp: new Date().toISOString(),
    ...eventData,
  };
  // Track the click event
  if (window.analytics) {
    window.analytics.track(
      eventName,
      clickData,
      enrichOptions(null)
    );
  }

  return clickData;
}

// ---- Reliable Customer Identify ----
function identifyCustomer(customerId, traits) {
  if (!customerId || !traits) return;

  try { localStorage.setItem('cid', customerId); } catch (e) {}

  function doIdentify() {
    try {
      // Get current Segment anonymous ID
      var currentAnonId = null;
      if (window.analytics && typeof window.analytics.user === 'function') {
        var user = window.analytics.user();
        if (user && typeof user.anonymousId === 'function') {
          currentAnonId = user.anonymousId();
        }
      }

      // Skip if already identified this customer with this anonymous ID
      var state = safeGet('__segment_identify_state__');
      if (state && state.customerId === customerId
          && state.anonymousId === currentAnonId && currentAnonId !== null) {
        return;
      }

      window.analytics.identify(customerId, traits, enrichOptions(null));

      // Set flag AFTER successful call
      safeSet('__segment_identify_state__', {
        customerId: customerId,
        anonymousId: currentAnonId,
        timestamp: Date.now()
      });
      try { localStorage.setItem('segment_identified', 'true'); } catch (e) {}
    } catch (err) {
      console.error('[session] identify failed:', err);
      // Do NOT set flag — allow retry on next page load
    }
  }

  // Use analytics.ready() to ensure SDK is loaded
  if (window.analytics && typeof window.analytics.ready === 'function') {
    window.analytics.ready(function() {
      setTimeout(doIdentify, 200);
    });
  }

  // Fallback if ready() never fires
  setTimeout(function() {
    var state = safeGet('__segment_identify_state__');
    if (!state || state.customerId !== customerId) {
      doIdentify();
    }
  }, 5000);
}

function clearIdentifyState() {
  safeDel('__segment_identify_state__');
  try {
    localStorage.removeItem('segment_identified');
    localStorage.removeItem('cid');
  } catch (e) {}
}

// ---- sendBeacon fallback (recovers events lost to fast bounces / blocked SDK) ----
//
// Events host is api.cdp.supertails.com — the SAME host the Segment SDK posts to
// (verified: /v1/t + /v1/p return 200). Do NOT derive it from analytics._cdn: that is
// evs.cdp.supertails.com (the library/settings CDN), which returns 404 for /v1/t and,
// combined with an application/json body, forced a CORS preflight the CDN rejected — so
// every previous beacon died with net::ERR_FAILED. The host is identical across prod and
// dev (only the write key differs), so a constant is safe.
var BEACON_API_BASE = 'https://api.cdp.supertails.com';

// Snapshot the write key NOW, while window.analytics is still the snippet stub that carries
// it. analytics-next drops _writeKey once it initialises, so reading it lazily inside the
// beacon can return undefined and silently no-op. The snippet sets it above us in <head>.
var BEACON_WRITE_KEY = (window.analytics && window.analytics._writeKey) || null;

// Segment's browser anonymousId. Prefer the live SDK; fall back to the ajs_anonymous_id
// cookie/localStorage so beacons fired BEFORE the SDK initialises still stitch to the
// correct anonymous profile (both are present from the snippet's first paint).
function getBeaconAnonymousId() {
  try {
    if (window.analytics && typeof window.analytics.user === 'function') {
      var user = window.analytics.user();
      if (user && typeof user.anonymousId === 'function') {
        var a = user.anonymousId();
        if (a) return a;
      }
    }
  } catch (e) {}
  try {
    var ls = localStorage.getItem('ajs_anonymous_id');
    if (ls) return ls.replace(/^"|"$/g, '');
  } catch (e) {}
  var ck = getCookie('ajs_anonymous_id');
  return ck ? decodeURIComponent(ck).replace(/^"|"$/g, '') : null;
}

// Stable id so the backend can dedupe a beacon against the SDK copy of the same event.
function makeBeaconMessageId() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return 'beacon-' + window.crypto.randomUUID();
    }
  } catch (e) {}
  return 'beacon-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
}

// Low-level immediate send. type 'page' -> /v1/p (page call), otherwise /v1/t (track).
// text/plain keeps this a CORS "simple request": no preflight, so it survives page unload.
function sendBeaconEvent(name, properties, userId, type, timestamp) {
  try {
    if (!navigator.sendBeacon) return false;
    var writeKey = BEACON_WRITE_KEY || (window.analytics && window.analytics._writeKey) || null;
    if (!writeKey) return false;

    var kind = type === 'page' ? 'page' : 'track';
    var ctx = getDeviceContext();
    ctx.traits = getUserTraits();
    var ts = timestamp || new Date().toISOString();

    var body = {
      type: kind,
      properties: properties || {},
      context: ctx,
      userId: userId || (properties && properties.user_id) || null,
      anonymousId: getBeaconAnonymousId(),
      messageId: makeBeaconMessageId(),
      timestamp: ts,
      sentAt: new Date().toISOString(),
      writeKey: writeKey
    };
    if (kind === 'page') {
      body.name = name;
    } else {
      body.event = name;
    }

    var path = kind === 'page' ? '/v1/p' : '/v1/t';
    var blob = new Blob([JSON.stringify(body)], { type: 'text/plain' });
    return navigator.sendBeacon(BEACON_API_BASE + path, blob);
  } catch (e) {
    console.error('[session] sendBeacon failed:', e);
    return false;
  }
}

// ---- Beacon queue: fire ONLY on page-hide, and ONLY if the SDK never delivered ----
//
// The old code fired a beacon immediately alongside every SDK call, so any event the SDK
// DID deliver was double-counted. Instead we queue the event and flush on page-hide: if the
// SDK initialised, it has (or will, via its own keepalive) delivered to api.cdp, so we skip
// the beacon (no duplicate). If the SDK never came up — the fast-bounce / blocked-script
// cohort inside in-app webviews — we beacon, recovering exactly the sessions we were losing.
var __beaconQueue = [];
var __beaconListenersAdded = false;
var __beaconsFlushed = false;

function enqueueBeacon(type, name, properties, userId) {
  __beaconQueue.push({
    type: type,
    name: name,
    properties: properties,
    userId: userId,
    ts: new Date().toISOString()
  });
  ensureBeaconFlushListener();
}

function flushBeaconQueue() {
  if (__beaconsFlushed) return;
  __beaconsFlushed = true;
  // SDK is up => it owns delivery; sending beacons too would double-count.
  if (window.analytics && window.analytics.initialized) return;
  for (var i = 0; i < __beaconQueue.length; i++) {
    var b = __beaconQueue[i];
    sendBeaconEvent(b.name, b.properties, b.userId, b.type, b.ts);
  }
}

function ensureBeaconFlushListener() {
  if (__beaconListenersAdded) return;
  __beaconListenersAdded = true;
  // pagehide = navigation/close (most reliable unload signal, incl. bfcache);
  // visibilitychange->hidden = tab switch / app backgrounding (the in-app-webview bounce).
  window.addEventListener('pagehide', flushBeaconQueue, { capture: true });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flushBeaconQueue();
  }, { capture: true });
}

// ---- Track with sendBeacon fallback (survives page redirect) ----
function trackWithBeacon(eventName, properties) {
  // 1. Fire via Segment SDK normally
  if (window.analytics && typeof window.analytics.track === 'function') {
    window.analytics.track(
      eventName,
      properties,
      enrichOptions(null)
    );
  }

  // 2. sendBeacon fallback — queued and flushed on page-hide so it survives the
  // redirect to the payment gateway, without duplicating the SDK track above.
  var userId = null;
  if (window.analytics && typeof window.analytics.user === 'function') {
    var user = window.analytics.user();
    if (user && typeof user.id === 'function') {
      userId = user.id();
    }
  }
  enqueueBeacon("track", eventName, properties, userId);
}

// Expose global
window.sessionCore = {
  setIdleMs,
  ensureSessionAndMaybeStart,
  getSessionFields,
  getMergedContext,
  updateUTMsFromURLIfChanged,
  updateAttributionFromURLIfChanged,
  parseAttributionFromURL,
  getCookie,
  trackPageView,
  trackLinkClick,
  identifyCustomer,
  clearIdentifyState,
  trackWithBeacon,
  getDeviceInfo,
  getUserTraits,
};

// Install global enrichment for Segment (queued + direct calls)
installSegmentDeviceInfoEnrichment();
initDeviceHints();
